import React, { useState, useEffect, useMemo, useRef } from "react";
import CustomCard from "../../components/CustomCard";
import CustomInput from "../../components/CustomInput";
import CustomDropdown from "../../components/CustomDropdown";
import CustomButton from "../../components/CustomButton";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createUrbanInfoMT5Account,
  createUserDocumentsMT5Account,
  fetchAndStoreServerPublicKey,
  getApiResponsePayload,
  getResponseCode,
} from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { Residence, UserDocumentSubType } from "../../utils/enums";
import countries from "world-countries";

export default function UrbanInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [address, setAddress] = useState("");
  const [workAddress, setWorkAddress] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  // 0 = Resident, 1 = Not a resident (matches backend App\Enums\Residence)
  const [residence, setResidence] = useState(null);
  const [profession, setProfession] = useState("");
  const [nationality, setNationality] = useState("");
  const [hasOtherNationality, setHasOtherNationality] = useState(false);
  const [otherNationality, setOtherNationality] = useState("");
  const [isPep, setIsPep] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [documentImage, setDocumentImage] = useState(null);
  const [documentImagePreview, setDocumentImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");

  // Create country options from world-countries library
  // Format: { value: "LB", label: "Lebanon (LB)" }
  // Exclude Israel (IL) from the list
  const countryOptions = useMemo(() => {
    return countries
      .filter((country) => country.cca2 !== 'il') // Remove Israel
      .map((country) => ({
        value: country.cca2, // ISO 3166-1 alpha-2 code (e.g., "LB")
        label: `${country.name.common} (${country.cca2})`, // Display: "Lebanon (LB)"
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
  }, []);

  // Helpers to map stored values to backend enum codes (keep in sync with mobile)
  const getAccountTypeCode = async (step1Data) => {
    // First, try to get account type from step1Data
    if (step1Data && step1Data.accountType) {
      let value = step1Data.accountType;
      
      // Handle different formats: object, string, or already a code
      if (typeof value === "object" && value !== null) {
        // It's an object like {value: "premium", label: "Premium Account"}
        value = value.value || value.label || value;
      } else if (typeof value === "string") {
        // It's a string like "premium" or "Premium Account"
        value = value.toLowerCase();
      }
      
      // Check if it's already a number (0, 1, 2)
      if (typeof value === "number") {
        if (value >= 0 && value <= 2) {
          return value;
        }
      }
      
      // Map string values to codes
      const map = {
        mini: 0,
        standard: 1,
        premium: 2,
        "mini account": 0,
        "standard account": 1,
        "premium account": 2,
      };
      
      const accountTypeCode = map[value];
      if (accountTypeCode !== undefined) {
        return accountTypeCode;
      } else {
      }
    } else {
    }
    
    // If not found in step1Data, try to get current account type from backend
    try {
      const { getHomePageInfos } = await import('../../utils/apiClient');
      const homePageResponse = await getHomePageInfos();
      const accountTypeValue = homePageResponse?.decryptedData?.DATA?.INTERNAL_TYPE || 
                              homePageResponse?.decryptedData?.DATA?.internal_type ||
                              homePageResponse?.data?.DATA?.INTERNAL_TYPE ||
                              homePageResponse?.data?.DATA?.internal_type;
      
      if (accountTypeValue !== null && accountTypeValue !== undefined) {
        const intValue = typeof accountTypeValue === 'number' ? accountTypeValue : parseInt(accountTypeValue, 10);
        if (!isNaN(intValue) && intValue >= 0 && intValue <= 2) {
          return intValue;
        }
      }
    } catch (error) {
      // Continue to default value
    }
    
    // Only default to 0 (Mini) if we can't get it from anywhere
    return 0;
  };

  const getUserTypeCode = (userType) => {
    if (!userType) return 0;
    const value = typeof userType === "object" ? userType.value : userType;
    const map = {
      individual: 0, // INDIVIDUAL
      company: 1,    // COMPANY
      ib: 3,         // BROKER
    };
    return map[value] ?? 0;
  };

  const getLeverageFromStep1 = async (step1Data) => {
    // First, try to get leverage from step1Data
    if (step1Data && step1Data.leverage) {
      const leverageValue =
        typeof step1Data.leverage === "object"
          ? step1Data.leverage.value
          : step1Data.leverage;
      if (leverageValue) {
        // Accept "1:100" or "100" - extract numeric part and convert to integer
        const str = leverageValue.toString();
        const match = str.match(/1:(\d+)/);
        const numericValue = match ? match[1] : str;
        const intValue = parseInt(numericValue, 10);
        if (!isNaN(intValue)) {
          return intValue;
        }
      }
    }
    
    // If not found in step1Data, try to get current leverage from backend
    try {
      const { getTradingInfos } = await import('../../utils/apiClient');
      const tradingResponse = await getTradingInfos();
      const leverageValue = tradingResponse?.decryptedData?.DATA?.LEVERAGE || 
                           tradingResponse?.decryptedData?.DATA?.leverage ||
                           tradingResponse?.data?.DATA?.LEVERAGE ||
                           tradingResponse?.data?.DATA?.leverage;
      
      if (leverageValue) {
        const intValue = parseInt(leverageValue, 10);
        if (!isNaN(intValue)) {
          return intValue;
        }
      }
    } catch (error) {
      // Continue to default value
    }
    
    // Only default to 100 if we can't get it from anywhere
    return 100;
  };

  const getBrokerIdFromStep1 = (step1Data) => {
    if (!step1Data) return null;
    return step1Data.brokerId || null;
  };

  const formatDate = (dateIsoString) => {
    if (!dateIsoString) return null;
    const d = new Date(dateIsoString);
    if (Number.isNaN(d.getTime())) return null;
    // Match Flutter's getBirthDateToServerFormat() - returns YYYY-MM-DD format only
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getGenderValue = (gender) => {
    if (!gender) return null;
    const raw = typeof gender === "object" ? gender.value : gender;
    if (!raw) return null;
    const value = raw.toString().toLowerCase();
    const map = {
      male: 0,
      female: 1,
    };
    return map[value] ?? null;
  };

  const getMaritalStatusCode = (maritalStatus) => {
    if (!maritalStatus) return null;
    const raw = typeof maritalStatus === "object" ? maritalStatus.value : maritalStatus;
    if (!raw) return null;
    const value = raw.toString().toLowerCase();
    const map = {
      single: 0,
      married: 1,
      divorced: 2,
      widowed: 3,
    };
    return map[value] ?? null;
  };

  // Always send browser back button from this page to Home instead of previous KYC step
  useEffect(() => {
    const handlePopState = () => {
      // Check window.location immediately (synchronous check)
      const targetPath = window.location.pathname;
      if (targetPath.includes('user-regulation')) {
        // User clicked back and is going to user-regulation - redirect to home
        // Use replaceState to modify current entry, then navigate
        window.history.replaceState(null, '', '/home');
        navigate("/home", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);
  
  // Critical: Monitor location changes and immediately redirect if we land on user-regulation
  // This catches React Router navigation that happens before popstate
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.includes('user-regulation')) {
      // Immediately redirect - this runs synchronously when location changes
      window.history.replaceState(null, '', '/home');
      navigate("/home", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    // Initialize server public key
    const initializeServerKey = async () => {
      try {
        await fetchAndStoreServerPublicKey();
      } catch (error) {
        // Failed to initialize - continue anyway
      }
    };
    initializeServerKey();

    // Load saved data from sessionStorage if exists
    const savedData = sessionStorage.getItem('urban_info_data');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        // Map the saved data back to state (handle both uppercase and lowercase formats)
        if (data.ADDRESS !== null && data.ADDRESS !== undefined) setAddress(data.ADDRESS || "");
        else if (data.address !== null && data.address !== undefined) setAddress(data.address || "");
        
        if (data.WORK_ADDRESS !== null && data.WORK_ADDRESS !== undefined) setWorkAddress(data.WORK_ADDRESS || "");
        else if (data.workAddress !== null && data.workAddress !== undefined) setWorkAddress(data.workAddress || "");
        
        if (data.COUNTRY !== null && data.COUNTRY !== undefined) setCountry(data.COUNTRY || "");
        else if (data.country !== null && data.country !== undefined) setCountry(data.country || "");
        
        if (data.CITY !== null && data.CITY !== undefined) setCity(data.CITY || "");
        else if (data.city !== null && data.city !== undefined) setCity(data.city || "");
        
        if (data.STATE !== null && data.STATE !== undefined) setState(data.STATE || "");
        else if (data.state !== null && data.state !== undefined) setState(data.state || "");
        
        if (data.ZIP_CODE !== null && data.ZIP_CODE !== undefined) setZipCode(data.ZIP_CODE || "");
        else if (data.zipCode !== null && data.zipCode !== undefined) setZipCode(data.zipCode || "");
        
        // Load RESIDENCE as numeric enum (0 = Resident, 1 = Not a resident)
        if (data.RESIDENCE !== null && data.RESIDENCE !== undefined) {
          const value = typeof data.RESIDENCE === "string" ? parseInt(data.RESIDENCE, 10) : data.RESIDENCE;
          setResidence(!isNaN(value) ? value : null);
        } else if (data.residence !== null && data.residence !== undefined) {
          const value = typeof data.residence === "string" ? parseInt(data.residence, 10) : data.residence;
          setResidence(!isNaN(value) ? value : null);
        }
        
        // Load PROFESSION
        if (data.PROFESSION !== null && data.PROFESSION !== undefined) setProfession(data.PROFESSION || "");
        else if (data.profession !== null && data.profession !== undefined) setProfession(data.profession || "");
        
        // Load NATIONALITY
        if (data.NATIONALITY !== null && data.NATIONALITY !== undefined) setNationality(data.NATIONALITY || "");
        else if (data.nationality !== null && data.nationality !== undefined) setNationality(data.nationality || "");
        
        // Load OTHER_NATIONALITY
        if (data.OTHER_NATIONALITY !== null && data.OTHER_NATIONALITY !== undefined) {
          setOtherNationality(data.OTHER_NATIONALITY || "");
          setHasOtherNationality(!!data.OTHER_NATIONALITY);
        } else if (data.otherNationality !== null && data.otherNationality !== undefined) {
          setOtherNationality(data.otherNationality || "");
          setHasOtherNationality(!!data.otherNationality);
        }
        
        // Load IS_PEP
        if (data.IS_PEP !== null && data.IS_PEP !== undefined) setIsPep(data.IS_PEP === true || data.IS_PEP === 1);
        else if (data.isPep !== null && data.isPep !== undefined) setIsPep(data.isPep === true || data.isPep === 1);
        
        // Load DOCUMENT_TYPE (for address proof)
        if (data.DOCUMENT_TYPE !== null && data.DOCUMENT_TYPE !== undefined) {
          const docTypeValue = typeof data.DOCUMENT_TYPE === "string" ? parseInt(data.DOCUMENT_TYPE, 10) : data.DOCUMENT_TYPE;
          if (!isNaN(docTypeValue) && docTypeValue >= 0 && docTypeValue <= 16) {
            setDocumentType(docTypeValue);
          }
        } else if (data.documentType !== null && data.documentType !== undefined) {
          const docTypeValue = typeof data.documentType === "string" ? parseInt(data.documentType, 10) : data.documentType;
          if (!isNaN(docTypeValue) && docTypeValue >= 0 && docTypeValue <= 16) {
            setDocumentType(docTypeValue);
          }
        }
      } catch (e) {
        // Failed to parse - clear invalid data
        sessionStorage.removeItem('urban_info_data');
      }
    }
  }, []);

  // Auto-save form data to sessionStorage whenever fields change
  // Only save if the step hasn't been completed yet (to prevent overwriting after successful save)
  useEffect(() => {
    // Don't auto-save if this step is already completed (data was successfully saved to backend)
    const isCompleted = sessionStorage.getItem('urban_info_completed') === 'true';
    if (isCompleted) {
      return; // Don't overwrite data after successful save
    }
    
    const formData = {
      ADDRESS: address,
      WORK_ADDRESS: workAddress,
      COUNTRY: country,
      CITY: city,
      STATE: state,
      ZIP_CODE: zipCode,
      RESIDENCE: residence,
      PROFESSION: profession,
      NATIONALITY: nationality,
      OTHER_NATIONALITY: hasOtherNationality ? otherNationality : null,
      IS_PEP: isPep,
      DOCUMENT_TYPE: documentType,
    };
    sessionStorage.setItem('urban_info_data', JSON.stringify(formData));
  }, [address, workAddress, country, city, state, zipCode, residence, profession, nationality, hasOtherNationality, otherNationality, isPep, documentType]);

  const handleNext = async () => {
    // Get email - Priority 1: from localStorage (saved after successful registration)
    let email = localStorage.getItem('registrationEmail');
    let step2Data = null;
    let step1Data = null;
    
    // Always try to load step2Data from sessionStorage (needed for personal information fields)
    const savedStep2Data = sessionStorage.getItem('registration_step2_data');
    if (savedStep2Data) {
      try {
        step2Data = JSON.parse(savedStep2Data);
        // If email not found yet, try to get it from step2Data
        if (!email && step2Data.email) {
          email = step2Data.email;
        }
      } catch (e) {
        // Failed to parse
        step2Data = null;
      }
    } else {
      // Try to load from localStorage as fallback
      const savedStep2DataLocal = localStorage.getItem('registration_step2_data');
      
      if (savedStep2DataLocal) {
        try {
          step2Data = JSON.parse(savedStep2DataLocal);
          // If email not found yet, try to get it from step2Data
          if (!email && step2Data.email) {
            email = step2Data.email;
          }
        } catch (e) {
          step2Data = null;
        }
      }
    }
    
    // Validate step2Data
    if (!step2Data || !step2Data.firstName || !step2Data.lastName || !step2Data.email) {
      setErrorPopupMessage(t("urbanInfo.errors.personalInfoMissing") || "Personal information is missing. Please go back to the personal information step and complete it, then try again.");
      setShowErrorPopup(true);
      setLoading(false);
      return;
    }
    
    // Priority 2: Fallback to userEmail in localStorage (saved during login)
    if (!email) {
      email = localStorage.getItem("userEmail");
    }
    
    if (!email) {
      setErrorPopupMessage(t("urbanInfo.errors.emailRequired") || "Email is required. Please complete registration first.");
      setShowErrorPopup(true);
      return;
    }

    // Load step 1 data (account type, user type, leverage, broker)
    const savedStep1Data = sessionStorage.getItem('registration_step1_data');
    if (savedStep1Data) {
      try {
        step1Data = JSON.parse(savedStep1Data);
      } catch (e) {
        step1Data = null;
      }
    }

    // Fallback: try to rebuild minimal step1Data from localStorage if missing
    if (!step1Data) {
      const storedAccountType = localStorage.getItem('registration_accountType');
      const storedUserType = localStorage.getItem('registration_userType');
      const storedBrokerId = localStorage.getItem('registrationBrokerId');
      const storedLeverage = localStorage.getItem('registration_step1_data');
      if (storedAccountType || storedUserType || storedBrokerId) {
        // Try to parse storedAccountType if it's a JSON string
        let parsedAccountType = storedAccountType;
        if (storedAccountType) {
          try {
            parsedAccountType = JSON.parse(storedAccountType);
          } catch (e) {
            // Not JSON, use as-is (might be a string like "premium")
            parsedAccountType = storedAccountType;
          }
        }
        
        step1Data = {
          accountType: parsedAccountType || null,
          userType: storedUserType ? (() => {
            try {
              return JSON.parse(storedUserType);
            } catch {
              return storedUserType;
            }
          })() : null,
          brokerId: storedBrokerId || null,
          leverage: storedLeverage ? (() => {
            try {
              const parsed = JSON.parse(storedLeverage);
              return parsed.leverage || null;
            } catch {
              return null;
            }
          })() : null,
        };
      }
    }
    
    // Also try to get from localStorage registration_step1_data as a final fallback
    if (!step1Data || !step1Data.accountType) {
      const localStorageStep1Data = localStorage.getItem('registration_step1_data');
      if (localStorageStep1Data) {
        try {
          const parsed = JSON.parse(localStorageStep1Data);
          if (parsed.accountType) {
            step1Data = step1Data || {};
            step1Data.accountType = parsed.accountType;
            step1Data.userType = step1Data.userType || parsed.userType;
            step1Data.leverage = step1Data.leverage || parsed.leverage;
            step1Data.brokerId = step1Data.brokerId || parsed.brokerId;
          }
        } catch (e) {
        }
      }
    }

    // Check if we have step2Data - if not, show error
    if (!step2Data) {
      setErrorPopupMessage(
        t("urbanInfo.errors.missingPersonalInfo") || 
        "Personal information is missing. Please go back to the personal information step and complete it, then try again."
      );
      setShowErrorPopup(true);
      setLoading(false);
      return;
    }

    // Validate that all Urban Info fields are filled (all required) - use red borders, not popup
    const newErrors = {};

    if (!state || state.trim() === "") {
      newErrors.state = true;
    }
    if (zipCode === null || zipCode === undefined || String(zipCode).trim() === "") {
      newErrors.zipCode = true;
    }
    if (!address || address.trim() === "") {
      newErrors.address = true;
    }
    if (!profession || profession.trim() === "") {
      newErrors.profession = true;
    }
    if (!workAddress || workAddress.trim() === "") {
      newErrors.workAddress = true;
    }
    if (residence === null || residence === undefined || residence === "") {
      newErrors.residence = true;
    }
    if (!city || city.trim() === "") {
      newErrors.city = true;
    }
    if (!country || country.trim() === "") {
      newErrors.country = true;
    }
    if (!nationality || nationality.trim() === "") {
      newErrors.nationality = true;
    }
    if (hasOtherNationality && (!otherNationality || otherNationality.trim() === "")) {
      newErrors.otherNationality = true;
    }
    if (documentType === null || documentType === undefined || documentType === "") {
      newErrors.documentType = true;
    }
    if (!documentImage) {
      newErrors.documentImage = true;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const accountTypeCode = await getAccountTypeCode(step1Data);
    const userTypeCode = getUserTypeCode(step1Data?.userType);
    const leverage = await getLeverageFromStep1(step1Data);
    const brokerId = getBrokerIdFromStep1(step1Data);

    const genderCode = getGenderValue(step2Data?.gender);
    const maritalCode = getMaritalStatusCode(step2Data?.maritalStatus);

    // React-phone-input-2 phoneNumber was saved in step 2 as "phoneNumber"
    let phoneValue = null;
    if (step2Data?.phoneNumber) {
      let cleaned = step2Data.phoneNumber.toString().trim().replace(/\D/g, "");
      if (cleaned && cleaned.length > 0) {
        phoneValue = String(cleaned);
      }
    }

    // Prepare user information data - MUST match Flutter structure exactly
    // Backend expects USER_INFORMATION with all fields that were set in previous steps
    // The backend processes F_NAME, L_NAME, etc. even if we're only updating urban info
    // Based on backend code: updateUserInformation processes all fields with ?? operator
    const userInformation = {
      // EMAIL is required to identify which user to update
      'EMAIL': email,
      'IS_CREATE': 0,  // 0 for urban info API call, 1 for document upload
      
      // Personal information fields (from step 2) - required by backend processing
      'F_NAME': step2Data?.firstName || null,
      'L_NAME': step2Data?.lastName || null,
      'FATHER_NAME': step2Data?.fatherName || null,
      'MOTHER_NAME': step2Data?.motherName || null,
      'BIRTH_DAY': step2Data?.dob ? formatDate(step2Data.dob) : null,
      'PLACE_OF_BIRTH': step2Data?.placeOfBirth || null,
      'GENDER': genderCode, // Integer: 0 = Male, 1 = Female
      'MARITAL': maritalCode, // Integer: 0 = Single, 1 = Married, 2 = Divorced, 3 = Widowed
      'SPOUSE_NAME': maritalCode === 1 ? (step2Data?.spouseFirstName || null) : null,
      'SPOUSE_PROFESSION': maritalCode === 1 ? (step2Data?.spouseProfession || null) : null,
      'PHONE': phoneValue,
      
      // Account type fields (from step 1) - required by backend
      'TYPE': userTypeCode, // Integer: 0 = INDIVIDUAL, 1 = COMPANY, 3 = BROKER
      'INTERNAL_TYPE': accountTypeCode, // Integer: 0 = Mini, 1 = Standard, 2 = Premium
      'DEFAULT_LEVERAGE': leverage, // Integer (always has a value, defaults to 100 if not found)
      'REFERAL_BROKER': brokerId && brokerId.trim() !== "" ? brokerId.trim() : null,
      
      // Urban information fields (current step)
      'ADDRESS': address && address.trim() !== "" ? address.trim() : null,
      'WORK_ADDRESS': workAddress && workAddress.trim() !== "" ? workAddress.trim() : null,
      'CITY': city && city.trim() !== "" ? city.trim() : null,
      'COUNTRY': country && country.trim() !== "" ? country.trim() : null,
      'STATE': state && state.trim() !== "" ? state.trim() : null,
      'ZIP_CODE': zipCode !== null && zipCode !== undefined && zipCode !== "" ? String(zipCode).trim() : null,
      'PROFESSION': profession && profession.trim() !== "" ? profession.trim() : null,
      'NATIONALITY': nationality && nationality.trim() !== "" ? nationality.trim() : null,
      'OTHER_NATIONALITY': hasOtherNationality && otherNationality && otherNationality.trim() !== "" ? otherNationality.trim() : null,
      
      // Enum fields - Backend expects integers
      'RESIDENCE': (residence !== null && residence !== undefined && residence !== '') 
        ? (typeof residence === 'number' ? residence : parseInt(residence, 10))
        : null,
      'IS_PEP': isPep ? 1 : 0, // Integer: 0 or 1
      'BENEFICAL_RIGHT': null, // Not set in urban info step
      
      // Optional fields that backend processes
      'IDENTIFICATION_NUMBER': null,
      'RISK_STATUS': 0, // Default to 0
    };
    
    // Ensure RESIDENCE is valid integer (0 or 1) or null
    if (userInformation.RESIDENCE !== null && userInformation.RESIDENCE !== undefined) {
      const residenceInt = typeof userInformation.RESIDENCE === 'number' 
        ? userInformation.RESIDENCE 
        : parseInt(userInformation.RESIDENCE, 10);
      if (isNaN(residenceInt) || (residenceInt !== 0 && residenceInt !== 1)) {
        userInformation.RESIDENCE = null;
      } else {
        userInformation.RESIDENCE = residenceInt;
      }
    }

    // Validate that we have at least EMAIL and some address information
    if (!email || email.trim() === "") {
      setErrorPopupMessage(t("urbanInfo.errors.emailRequired") || "Email is required. Please complete registration first.");
      setShowErrorPopup(true);
      return;
    }
    

    setLoading(true);

    try {
      // Call real API to send the same USER_INFORMATION structure as mobile app
      const response = await createUrbanInfoMT5Account(userInformation);

      // Check HTTP status code first
      if (response.status && response.status >= 400) {
        let errorMessage = "An error occurred while saving urban information. Please try again.";
        
        // Try to extract error message from response
        if (response.data) {
          const errorData = typeof response.data === 'string' ? { message: response.data } : response.data;
          errorMessage = errorData?.MESSAGE || 
                        errorData?.message || 
                        errorData?.error || 
                        errorData?.ERROR ||
                        errorMessage;
          
          if (typeof errorMessage === 'string' && errorMessage.includes('Residence::from')) {
            errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
          }
        }
        
        setErrorPopupMessage(errorMessage);
        setShowErrorPopup(true);
        setLoading(false);
        return;
      }

      const payload = getApiResponsePayload(response);
      const responseCode = getResponseCode(response);

      if (responseCode === AppConstants.responseOk) {
        let userId = null;
        if (payload) {
          userId = payload.USER_ID || payload.userId || payload.user_id;
          if (!userId && payload.DATA) {
            userId = payload.DATA.USER_ID || payload.DATA.userId || payload.DATA.user_id;
          }
        }
        
        // Save user_id to localStorage if found
        if (userId !== null && userId !== undefined) {
          const userIdValue = typeof userId === 'number' ? userId : parseInt(userId, 10);
          if (!isNaN(userIdValue)) {
            localStorage.setItem('userId', String(userIdValue));
            localStorage.setItem('user_id', String(userIdValue));
            localStorage.setItem('USER_ID', String(userIdValue));
          }
        } else {
        }
        
        // Upload address document if available (matches Flutter's flow)
        if (documentImage && documentType !== null && documentType !== undefined) {
          try {
            const documentTypeValue = typeof documentType === 'number' 
              ? documentType 
              : (typeof documentType === 'object' && documentType?.value !== undefined 
                  ? documentType.value 
                  : parseInt(documentType, 10));
            
            const documentsResponse = await createUserDocumentsMT5Account(
              email,
              documentTypeValue,
              documentImage
            );
            
            const documentsResponseCode = documentsResponse?.decryptedData?.CODE || documentsResponse?.data?.CODE;
            
            if (documentsResponseCode !== AppConstants.responseOk) {
            }
          } catch (documentsError) {
            // Don't block navigation if document upload fails - urban info was already saved
          }
        }
        
        // Mark urban info as completed (don't clear data yet - keep it for potential back navigation)
        sessionStorage.setItem('urban_info_completed', 'true');
        // Note: We keep urban_info_data in sessionStorage in case user needs to navigate back
        // It will be cleared when they complete the next step or explicitly navigate away
        
        // Navigate directly to identity verification page (no popup)
        navigate("/identity-verification");
      } else {
        // Error - show error message
        // Extract error message from various possible locations
        let errorMessage = null;
        
        if (payload) {
          errorMessage = payload.MESSAGE ||
                        payload.message ||
                        payload.error ||
                        payload.ERROR ||
                        payload.data?.MESSAGE ||
                        payload.data?.message ||
                        payload.body?.MESSAGE ||
                        payload.body?.message;
        }
        
        // Try response.data if not found in decryptedData
        if (!errorMessage && response.data) {
          errorMessage = response.data.MESSAGE ||
                        response.data.message ||
                        response.data.error ||
                        response.data.ERROR ||
                        response.data.body?.MESSAGE ||
                        response.data.body?.message;
        }
        
        // If still no message, provide specific message based on response code
        if (!errorMessage) {
          if (responseCode === AppConstants.responseFailed) {
            // Code 207 - Backend returned responseFailed, which means an exception occurred
            
            // Try to extract detailed error message from response
            let detailedError = null;
            if (payload) {
              detailedError = payload.MESSAGE || payload.message || payload.ERROR || payload.error;
              if (payload.data) {
                detailedError = detailedError || payload.data.MESSAGE || payload.data.message;
              }
            }
            if (response.data && !detailedError) {
              detailedError = response.data.MESSAGE || response.data.message || response.data.ERROR || response.data.error;
            }
            
            const hasRegistrationEmail = localStorage.getItem('registrationEmail');
            const hasUserRegulationCompleted = sessionStorage.getItem('user_regulation_completed');
            
            if (!hasRegistrationEmail) {
              errorMessage = "Failed to save urban information. Please complete the personal information step first, then try again.";
            } else if (!hasUserRegulationCompleted) {
              errorMessage = "Failed to save urban information. Please complete the user regulation step first, then try again.";
            } else if (detailedError) {
              // Use backend error message if available
              errorMessage = `Failed to save urban information: ${detailedError}`;
            } else {
              // Code 207 means backend exception was caught
              // Check if RESIDENCE was sent correctly
              const residenceValue = userInformation.RESIDENCE;
              if (residenceValue === undefined || residenceValue === null) {
                errorMessage = "Failed to save urban information. Please select a residence status (Resident or Not a resident).";
              } else {
                errorMessage = "Failed to save urban information. The backend encountered an error. Please check that all fields are filled correctly and try again. If the problem persists, contact support.";
              }
            }
          } else if (responseCode === AppConstants.responseError) {
            errorMessage = "An error occurred while saving urban information. Please try again.";
          } else if (responseCode === AppConstants.invalidInputs) {
            errorMessage = "Invalid input data. Please check all fields and try again.";
          } else {
            errorMessage = `Failed to save urban information (Error Code: ${responseCode}). Please try again.`;
          }
        }
        
        // If error message contains backend error details, extract and format it
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
        
        // Check if there's a more detailed error in the response
        if (response.data && typeof response.data === 'string' && response.data.includes('Residence::from')) {
          errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
        }
        
        // Check if error message is a string that contains error details
        if (typeof errorMessage === 'string') {
          if (errorMessage.includes('Residence::from')) {
            errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
          } else if (errorMessage.length > 200) {
            // If error message is very long, it might be a full error stack - extract meaningful part
            const match = errorMessage.match(/([^:]+::[^:]+)/);
            if (match) {
              errorMessage = `Error: ${match[1]}. Please check your input and try again.`;
            }
          }
        }
        
        // In development, include response code in error message for debugging
        if (process.env.NODE_ENV === 'development') {
          const debugInfo = `\n\n[Debug Info: Response Code ${responseCode}]`;
          errorMessage = errorMessage + debugInfo;
        }
        
        setErrorPopupMessage(errorMessage);
        setShowErrorPopup(true);
      }
    } catch (error) {
      let errorMessage = "An error occurred while saving urban information. Please try again.";
      
      if (error.response?.data) {
        try {
          const errorData = error.response.data;
          
          // Try to extract error message from various locations
          errorMessage = errorData?.MESSAGE || 
                        errorData?.message || 
                        errorData?.error || 
                        errorData?.ERROR ||
                        errorMessage;
          
          // Check if error message contains backend validation error
          if (typeof errorMessage === 'string' && errorMessage.includes('Residence::from')) {
            errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
          }
        } catch (e) {
          // Failed to parse error data
        }
      } else if (error.message) {
        // Fallback to error message if available
        if (error.message.includes('Residence::from')) {
          errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

//       //       //       //       //       :', JSON.stringify(response.data, null, 2));

//       // Check HTTP status code first
//       if (response.status && response.status >= 400) {
//         //         let errorMessage = "An error occurred while saving urban information. Please try again.";
        
//         // Try to extract error message from response
//         if (response.data) {
//           const errorData = typeof response.data === 'string' ? { message: response.data } : response.data;
//           errorMessage = errorData?.MESSAGE || 
//                         errorData?.message || 
//                         errorData?.error || 
//                         errorData?.ERROR ||
//                         errorMessage;
          
//           if (typeof errorMessage === 'string' && errorMessage.includes('Residence::from')) {
//             errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
//           }
//         }
        
//         setErrorPopupMessage(errorMessage);
//         setShowErrorPopup(true);
//         setLoading(false);
//         return;
//       }

//       let decryptedData = response.decryptedData;
//       //       //       // If decryptedData is a string, try to parse it or extract CODE from it
//       if (typeof decryptedData === 'string') {
//         //         try {
//           decryptedData = JSON.parse(decryptedData);
//           //         } catch (e) {
//           //           const codeMatch = decryptedData.match(/"CODE"\s*:\s*(\d+)/);
//           if (codeMatch) {
//             decryptedData = { CODE: parseInt(codeMatch[1]) };
//             //           } else {
//             const codeMatch2 = decryptedData.match(/CODE["\s]*[:=]\s*(\d+)/i);
//             if (codeMatch2) {
//               decryptedData = { CODE: parseInt(codeMatch2[1]) };
//               //             } else {
//               //             }
//           }
//         }
//       }

//       //       :', JSON.stringify(decryptedData, null, 2));

//       // Check response code - try multiple locations
//       let responseCode = null;
//       if (decryptedData) {
//         // Try direct CODE property
//         responseCode = decryptedData.CODE || decryptedData.code;
//         //         // Try nested structures
//         if (responseCode === null || responseCode === undefined) {
//           if (decryptedData.body) {
//             responseCode = decryptedData.body.CODE || decryptedData.body.code;
//             //           }
//           if ((responseCode === null || responseCode === undefined) && decryptedData.data) {
//             responseCode = decryptedData.data.CODE || decryptedData.data.code;
//             //           }
//           // Try response.data structure (from axios response)
//           if ((responseCode === null || responseCode === undefined) && response.data) {
//             responseCode = response.data.CODE || response.data.code;
//             //           }
//           // Try response.data.body structure
//           if ((responseCode === null || responseCode === undefined) && response.data?.body) {
//             responseCode = response.data.body.CODE || response.data.body.code;
//             //           }
//         }
//       }

//       // Also check the raw response structure
//       if ((responseCode === null || responseCode === undefined) && response.data) {
//         responseCode = response.data.CODE || response.data.code;
//         //       }

//       if (responseCode !== null && responseCode !== undefined) {
//         responseCode = typeof responseCode === 'string' ? parseInt(responseCode, 10) : responseCode;
//       }

//       //       :', AppConstants.responseOk);
//       //       if (responseCode === AppConstants.responseOk) {
//         // Success
//         //         setShowSuccessPopup(true);
//         // Clear saved data after successful submission
//         sessionStorage.removeItem('urban_info_data');
//       } else {
//         // Error - show error message
//         //         ,
//           responseData: JSON.stringify(response.data, null, 2),
//           fullResponse: JSON.stringify(response, null, 2),
//           MESSAGE: decryptedData?.MESSAGE,
//           message: decryptedData?.message,
//           error: decryptedData?.error,
//           ERROR: decryptedData?.ERROR,
//           dataMESSAGE: decryptedData?.data?.MESSAGE,
//           dataMessage: decryptedData?.data?.message,
//           responseDataMESSAGE: response.data?.MESSAGE,
//           responseDataMessage: response.data?.message,
//           allKeys: decryptedData ? Object.keys(decryptedData) : [],
//           responseDataKeys: response.data ? Object.keys(response.data) : []
//         });
        
//         // Extract error message from various possible locations
//         let errorMessage = null;
        
//         // Try decryptedData first
//         if (decryptedData) {
//           errorMessage = decryptedData.MESSAGE || 
//                         decryptedData.message || 
//                         decryptedData.error || 
//                         decryptedData.ERROR ||
//                         decryptedData.data?.MESSAGE ||
//                         decryptedData.data?.message ||
//                         decryptedData.body?.MESSAGE ||
//                         decryptedData.body?.message;
//         }
        
//         // Try response.data if not found in decryptedData
//         if (!errorMessage && response.data) {
//           errorMessage = response.data.MESSAGE ||
//                         response.data.message ||
//                         response.data.error ||
//                         response.data.ERROR ||
//                         response.data.body?.MESSAGE ||
//                         response.data.body?.message;
//         }
        
//         // If still no message, provide specific message based on response code
//         if (!errorMessage) {
//           // Try to extract any error information from the response
//           const responseString = JSON.stringify(response.data || decryptedData || {});
//           //           // Check for common error patterns in the response
//           if (responseString.includes('validation') || responseString.includes('Validation')) {
//             errorMessage = "Validation error. Please check all required fields are filled correctly.";
//           } else if (responseString.includes('required') || responseString.includes('Required')) {
//             errorMessage = "Some required fields are missing. Please fill in all required fields.";
//           } else if (responseString.includes('invalid') || responseString.includes('Invalid')) {
//             errorMessage = "Invalid data provided. Please check your input and try again.";
//           } else if (responseCode === AppConstants.responseFailed) {
//             // Code 207 - Check if user has completed registration
//             const hasRegistrationEmail = localStorage.getItem('registrationEmail');
//             if (!hasRegistrationEmail) {
//               errorMessage = "Failed to save urban information. Please complete the personal information step first, then try again.";
//             } else {
//               // Code 207 typically means the operation failed but backend doesn't provide specific reason
//               // This could be due to:
//               // 1. Account not fully set up yet (timing issue) - MT5 account may need a moment to be ready
//               // 2. Backend validation failing silently
//               // 3. Account state issue
//               // 4. RESIDENCE field validation (even though it's optional, backend might have specific requirements)
//               const residenceValue = userInformation.RESIDENCE;
//               if (residenceValue === null || residenceValue === undefined) {
//                 errorMessage = "Failed to save urban information. The account may not be fully set up yet, or there may be a validation issue. Please wait a few moments and try again. If the problem persists, try entering a residence code or contact support.";
//               } else {
//                 errorMessage = "Failed to save urban information. The account may not be fully set up yet, or there may be a validation issue. Please wait a few moments and try again. If the problem persists, contact support.";
//               }
//             }
//           } else if (responseCode === AppConstants.responseError) {
//             errorMessage = "An error occurred while saving urban information. Please try again.";
//           } else if (responseCode === AppConstants.invalidInputs) {
//             errorMessage = "Invalid input data. Please check all fields and try again.";
//           } else {
//             errorMessage = `Failed to save urban information (Error Code: ${responseCode}). Please try again.`;
//           }
//         }
        
//         // If error message contains backend error details, extract and format it
//         if (typeof errorMessage === 'object') {
//           errorMessage = JSON.stringify(errorMessage);
//         }
        
//         // Check if there's a more detailed error in the response
//         if (response.data && typeof response.data === 'string' && response.data.includes('Residence::from')) {
//           errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
//         }
        
//         // Check if error message is a string that contains error details
//         if (typeof errorMessage === 'string') {
//           if (errorMessage.includes('Residence::from')) {
//             errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
//           } else if (errorMessage.length > 200) {
//             // If error message is very long, it might be a full error stack - extract meaningful part
//             const match = errorMessage.match(/([^:]+::[^:]+)/);
//             if (match) {
//               errorMessage = `Error: ${match[1]}. Please check your input and try again.`;
//             }
//           }
//         }
        
//         //         // In development, include response code in error message for debugging
//         if (process.env.NODE_ENV === 'development') {
//           const debugInfo = `\n\n[Debug Info: Response Code ${responseCode}]`;
//           errorMessage = errorMessage + debugInfo;
//         }
        
//         setErrorPopupMessage(errorMessage);
//         setShowErrorPopup(true);
//       }
//     } catch (error) {
//       //       //       //       //       //       let errorMessage = "An error occurred while saving urban information. Please try again.";
      
//       if (error.response?.data) {
//         try {
//           const errorData = error.response.data;
//           //           // Try to extract error message from various locations
//           errorMessage = errorData?.MESSAGE || 
//                         errorData?.message || 
//                         errorData?.error || 
//                         errorData?.ERROR ||
//                         errorMessage;
          
//           // Check if error message contains backend validation error
//           if (typeof errorMessage === 'string' && errorMessage.includes('Residence::from')) {
//             errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
//           }
          
//           //         } catch (e) {
//           //         }
//       } else if (error.message) {
//         // Fallback to error message if available
//         if (error.message.includes('Residence::from')) {
//           errorMessage = "Invalid residence value. Please enter a valid residence code (numeric value).";
//         } else {
//           errorMessage = error.message;
//         }
//       }
      
//       setErrorPopupMessage(errorMessage);
//       setShowErrorPopup(true);
//     } finally {
//       setLoading(false);
//     }
//   };


  return (
    <div className="page-container">
      <CustomCard>
        <div className="signup-content">
          <h1 className="signup-title">{t("urbanInfo.title") || "Urban Information"}</h1>

          {/* Progress Indicator - Steps 1 and 2 active (user regulation completed) */}
          <div className="progress-indicator">
            <div className="progress-circle active">
              {t("userRegulation.progress.step1") || "01"}
            </div>
            <div className="progress-line progress-line-active" />
            <div className="progress-circle active">
              {t("userRegulation.progress.step2") || "02"}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("userRegulation.progress.step3") || "03"}
            </div>
          </div>

          {/* Form - Order matches Flutter _buildStep3Content() exactly */}
          <div className="signup-form">
            {/* 1. State */}
            <div className="form-group">
              <label>{t("urbanInfo.state.label") || "State"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-map"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("urbanInfo.state.placeholder") || "Enter your state"}
                  className={`input-box ${errors.state ? "input-error" : ""}`}
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    if (errors.state) {
                      setErrors({ ...errors, state: null });
                    }
                  }}
                />
              </div>
            </div>

            {/* 2. ZIP Code */}
            <div className="form-group">
              <label>{t("urbanInfo.zipCode.label") || "ZIP Code"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-mail-bulk"></i>
                </span>
                <input
                  type="number"
                  placeholder={t("urbanInfo.zipCode.placeholder") || "Enter your ZIP code"}
                  className={`input-box ${errors.zipCode ? "input-error" : ""}`}
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    if (errors.zipCode) {
                      setErrors({ ...errors, zipCode: null });
                    }
                  }}
                />
              </div>
            </div>

            {/* 3. Address */}
            <div className="form-group">
              <label>{t("urbanInfo.address.label") || "Address"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-map-marker-alt"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("urbanInfo.address.placeholder") || "Enter your address"}
                  className={`input-box ${errors.address ? "input-error" : ""}`}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    if (errors.address) {
                      setErrors({ ...errors, address: null });
                    }
                  }}
                />
              </div>
            </div>

            {/* 4. Profession */}
            <div className="form-group">
              <label>{t("urbanInfo.profession.label") || "Profession"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-briefcase"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("urbanInfo.profession.placeholder") || "Enter your profession"}
                  className={`input-box ${errors.profession ? "input-error" : ""}`}
                  value={profession}
                  onChange={(e) => {
                    setProfession(e.target.value);
                    if (errors.profession) {
                      setErrors({ ...errors, profession: null });
                    }
                  }}
                />
              </div>
            </div>

            {/* 5. Work Address */}
            <div className="form-group">
              <label>{t("urbanInfo.workAddress.label") || "Work Address"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-briefcase"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("urbanInfo.workAddress.placeholder") || "Enter your work address"}
                  className={`input-box ${errors.workAddress ? "input-error" : ""}`}
                  value={workAddress}
                  onChange={(e) => {
                    setWorkAddress(e.target.value);
                    if (errors.workAddress) {
                      setErrors({ ...errors, workAddress: null });
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t("urbanInfo.residence.label") || "Residence"}</label>
              <div className="input-wrapper">
              <div className="dropdown-wrapper">
                <CustomDropdown
                  options={[
                    {
                      value: Residence.RESIDENT_INDIVIDUAL,
                      label:
                        t("urbanInfo.residence.options.resident") ||
                        "Resident",
                    },
                    {
                      value: Residence.NOT_RESIDENT,
                      label:
                        t("urbanInfo.residence.options.nonResident") ||
                        "Not a resident",
                    },
                  ]}
                  value={
                    residence === Residence.RESIDENT_INDIVIDUAL ||
                    residence === Residence.NOT_RESIDENT
                      ? {
                          value: residence,
                          label:
                            residence === Residence.RESIDENT_INDIVIDUAL
                              ? t("urbanInfo.residence.options.resident") ||
                                "Resident"
                              : t(
                                  "urbanInfo.residence.options.nonResident"
                                ) || "Not a resident",
                        }
                      : null
                  }
                  onChange={(selected) => {
                    const selectedValue =
                      selected && (selected.value === Residence.RESIDENT_INDIVIDUAL ||
                      selected.value === Residence.NOT_RESIDENT)
                        ? selected.value
                        : null;
                    setResidence(selectedValue);
                    if (errors.residence) {
                      setErrors({ ...errors, residence: null });
                    }
                  }}
                  placeholder={
                    t("urbanInfo.residence.placeholder") ||
                    "Select residence status"
                  }
                  className={errors.residence ? "dropdown-error" : ""}
                />
              </div>
              </div>
            </div>

            {/* 7. City */}
            <div className="form-group">
              <label>{t("urbanInfo.city.label") || "City"}</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <i className="fa-solid fa-city"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("urbanInfo.city.placeholder") || "Enter your city"}
                  className={`input-box ${errors.city ? "input-error" : ""}`}
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    if (errors.city) {
                      setErrors({ ...errors, city: null });
                    }
                  }}
                />
              </div>
            </div>

            {/* 8. Country */}
            <div className="form-group">
              <label>{t("urbanInfo.country.label") || "Country"}</label>
              <div className="input-wrapper">
                <div className="dropdown-wrapper">
                  <CustomDropdown
                    options={countryOptions}
                    value={
                      country
                        ? {
                            value: country,
                            label:
                              countryOptions.find((opt) => opt.value === country)?.label ||
                              country,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      setCountry(selected ? selected.value : "");
                      if (errors.country) {
                        setErrors({ ...errors, country: null });
                      }
                    }}
                    placeholder={t("urbanInfo.country.placeholder") || "Select your country"}
                    searchable={true}
                    className={errors.country ? "dropdown-error" : ""}
                  />
                </div>
              </div>
            </div>

            {/* 9. Nationality */}
            <div className="form-group">
              <label>{t("urbanInfo.nationality.label") || "Nationality"}</label>
              <div className="input-wrapper">
                <div className="dropdown-wrapper">
                  <CustomDropdown
                    options={countryOptions}
                    value={
                      nationality
                        ? {
                            value: nationality,
                            label:
                              countryOptions.find((opt) => opt.value === nationality)?.label ||
                              nationality,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      setNationality(selected ? selected.value : "");
                      if (errors.nationality) {
                        setErrors({ ...errors, nationality: null });
                      }
                    }}
                    placeholder={t("urbanInfo.nationality.placeholder") || "Select your nationality"}
                    searchable={true}
                    className={errors.nationality ? "dropdown-error" : ""}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  checked={hasOtherNationality}
                  onChange={(e) => {
                    setHasOtherNationality(e.target.checked);
                    if (!e.target.checked) {
                      setOtherNationality("");
                    }
                  }}
                  className="custom-checkbox"
                />
                <label onClick={() => {
                  setHasOtherNationality(!hasOtherNationality);
                  if (hasOtherNationality) {
                    setOtherNationality("");
                  }
                }}>
                  {t("urbanInfo.otherNationality.checkbox") || "Have other nationality"}
                </label>
              </div>
            </div>

            {hasOtherNationality && (
              <div className="form-group">
                <label>{t("urbanInfo.otherNationality.label") || "Other Nationality"}</label>
                <div className="input-wrapper">
                  <div className="dropdown-wrapper">
                    <CustomDropdown
                      options={countryOptions}
                      value={
                        otherNationality
                          ? {
                              value: otherNationality,
                              label:
                                countryOptions.find((opt) => opt.value === otherNationality)?.label ||
                                otherNationality,
                            }
                          : null
                      }
                      onChange={(selected) => {
                        setOtherNationality(selected ? selected.value : "");
                        if (errors.otherNationality) {
                          setErrors({ ...errors, otherNationality: null });
                        }
                      }}
                      placeholder={t("urbanInfo.otherNationality.placeholder") || "Select your other nationality"}
                      searchable={true}
                      className={errors.otherNationality ? "dropdown-error" : ""}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  checked={isPep}
                  onChange={(e) => setIsPep(e.target.checked)}
                  className="custom-checkbox"
                />
                <label onClick={() => setIsPep(!isPep)}>
                  {t("urbanInfo.isPep.label") || "Are you a PEP (Politically Exposed Person)?"}
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>{t("urbanInfo.documentType.label") || "Address Document Type"}</label>
              <div className="input-wrapper">
                <div className="dropdown-wrapper">
                  <CustomDropdown
                    options={[
                      { value: UserDocumentSubType.UTILITY_BILL, label: t("urbanInfo.documentType.options.utilityBill") || "Utility Bill" },
                      { value: UserDocumentSubType.CERTIFICATE_OF_RESIDENCE, label: t("urbanInfo.documentType.options.certificateOfResidence") || "Certificate of Residence" },
                    ]}
                    value={
                      documentType !== null && documentType !== undefined
                        ? (() => {
                            // Find the enum key for this value
                            const enumKey = Object.keys(UserDocumentSubType).find(key => UserDocumentSubType[key] === documentType);
                            if (!enumKey) {
                              return { value: documentType, label: `Document Type ${documentType}` };
                            }
                            
                            // Convert enum key (e.g., "CERTIFICATE_OF_RESIDENCE") to camelCase (e.g., "certificateOfResidence")
                            const toCamelCase = (str) => {
                              return str.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                            };
                            
                            const translationKey = `urbanInfo.documentType.options.${toCamelCase(enumKey)}`;
                            const translatedLabel = t(translationKey);
                            
                            // If translation exists and is different from the key, use it; otherwise use fallback
                            const label = (translatedLabel && translatedLabel !== translationKey) 
                              ? translatedLabel 
                              : (documentType === UserDocumentSubType.UTILITY_BILL 
                                  ? "Utility Bill" 
                                  : documentType === UserDocumentSubType.CERTIFICATE_OF_RESIDENCE 
                                    ? "Certificate of Residence" 
                                    : `Document Type ${documentType}`);
                            
                            return { value: documentType, label };
                          })()
                        : null
                    }
                    onChange={(selected) => {
                      setDocumentType(selected && selected.value !== null && selected.value !== undefined ? selected.value : null);
                      if (errors.documentType) {
                        setErrors({ ...errors, documentType: null });
                      }
                    }}
                    placeholder={t("urbanInfo.documentType.placeholder") || "Select document type"}
                    className={errors.documentType ? "dropdown-error" : ""}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>{t("urbanInfo.documentImage.label") || "Address Document Image"}</label>
              <div className={`file-upload-wrapper ${errors.documentImage ? "input-error" : ""}`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setDocumentImage(file);
                      if (errors.documentImage) {
                        setErrors({ ...errors, documentImage: null });
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setDocumentImagePreview(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="file-input"
                  id="documentImageInput"
                />
                <label htmlFor="documentImageInput" className="file-upload-area">
                  {documentImagePreview ? (
                    <div className="image-preview-container">
                      <img 
                        src={documentImagePreview} 
                        alt={t("urbanInfo.documentImage.preview") || "Document preview"} 
                        className="uploaded-image"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDocumentImage(null);
                          setDocumentImagePreview(null);
                          const input = document.getElementById("documentImageInput");
                          if (input) input.value = "";
                        }}
                        className="remove-image-btn"
                        aria-label="Remove image"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                      <button
                        type="button"
                        className="upload-button-circle change-image-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          document.getElementById("documentImageInput")?.click();
                        }}
                        aria-label="Change image"
                      >
                        <i className="fa-solid fa-camera"></i>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="camera-icon-placeholder">
                        <i className="fa-solid fa-camera"></i>
                      </div>
                      <button
                        type="button"
                        className="upload-button-circle"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("documentImageInput")?.click();
                        }}
                        aria-label="Upload image"
                      >
                        <i className="fa-solid fa-camera"></i>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                      <span className="document-image-text">
                        {t("urbanInfo.documentImage.label") || "Document Image"}
                      </span>
                    </>
                  )}
                </label>
              </div>
            </div>

            <CustomButton 
              className="next-button" 
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? (t("common.loading") || "Loading...") : (t("urbanInfo.nextButton") || "Next")}
            </CustomButton>
          </div>
        </div>
      </CustomCard>

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title={t("common.error") || "Error"}
        message={errorPopupMessage}
        buttonText={t("common.ok") || "OK"}
        icon="fa-circle-exclamation"
      />

    </div>
  );
}